import type { IDynamicMarketTicketResponse } from '@/interface/IDynamicMarketBetSlip';
import type { IDynamicMarketOrderResponse, IOrderResponse } from '@/interface/IOrderResponse';
import type { IPrice } from '@/interface/IPrice';
import { IScore } from '@/interface/IScore';
import type { ITicketMpResponse, ITicketOddsInfo, ITicketResponse } from '@/interface/ITicketResponse';
import type { IBetBuilderBetSlipMarket, IBetBuilderOrderResponse, IBetBuilderSelectedSelection, IBetBuilderTicketResponse, IBetSlipSelection } from '@/interface/betBuilder';
import { BetBuilderSelectionStatus, BetBuilderTicketStatus, OpenTicketStatus, PlaceOrderStatus, PriceStyle, StatementDetailStatus } from '@/interface/enum';
import type {
    BetBuilderMarketResponse,
    BetBuilderMarketSelectionResponse,
    BetBuilderOrderResponse,
    BetBuilderTicketResponse,
    DynamicMarketOrderResponse,
    DynamicMarketTicketResponse,
    OrderResponse,
    PriceResponse,
    SelectedMarketSelectionResponse,
    TicketMpResponse,
    TicketOddsInfo,
    TicketResponse,
} from '@/interface/pollux';
import { getLastStake } from '@/libraries/betSlip/lastStakeStorage';

export function isOddsClose(statusCode: OpenTicketStatus | undefined) {
    switch (statusCode) {
        case OpenTicketStatus.EventClosed:
        case OpenTicketStatus.EventCompleted:
        case OpenTicketStatus.EventInternal:
            return {
                shouldRemove: true,
                message: 'betSlip_errorMessage_eventClosed',
            };
        case OpenTicketStatus.OddsClosed:
        case OpenTicketStatus.OddsCompleted:
        case OpenTicketStatus.OddsInternal:
            return {
                shouldRemove: true,
                message: 'betSlip_errorMessage_oddsClosed',
            };
        default:
            return {
                shouldRemove: false,
                message: '',
            };
    }
}

export function getDefaultStake(minBet: number, maxBet: number, currentStake = 0) {
    if (Number.isNaN(maxBet)) throw new Error('calculateStake:maxBet is NaN');
    if (Number.isNaN(minBet)) throw new Error('calculateStake:minBet is NaN');
    const defaultStake = getLastStake();

    const newStake = currentStake || defaultStake || 0;
    if (Number.isNaN(newStake)) throw new Error(`calculateStake:newStake is NaN:${currentStake}:${defaultStake}`);

    if (newStake < minBet) return minBet;
    if (newStake > maxBet) return maxBet;
    return newStake;
}

export function isPlaceHierarchyClosed(orderStatus: PlaceOrderStatus) {
    // ticket
    const isMemberClosed = orderStatus === PlaceOrderStatus.MemberClosed;
    const isHierarchyClosed = orderStatus === PlaceOrderStatus.HierarachyClosed;
    return isMemberClosed || isHierarchyClosed;
}

export function formatTicket(ticketResponse: TicketResponse): ITicketResponse {
    const oddsInfo = Object.fromEntries(Object.entries(ticketResponse.oddsInfo ?? {})
        .map(([key, oddsInfo]) => [key, formatTicketOddsInfo(oddsInfo)]));

    return {
        minBet: ticketResponse.minBet,
        maxBet: ticketResponse.maxBet,
        balance: ticketResponse.balance ?? null,
        oddsInfo,
        errorCode: OpenTicketStatus.fromPollux(ticketResponse.errorCode),
    };
}

export function formatMpTicket(ticketMpResponse: TicketMpResponse): ITicketMpResponse {
    let oddsInfo: Record<string, ITicketOddsInfo> | undefined;
    if (ticketMpResponse.oddsInfo) {
        oddsInfo = Object.fromEntries(Object.entries(ticketMpResponse.oddsInfo ?? {})
            .map(([key, oddsInfo]) => [key, formatTicketOddsInfo(oddsInfo)]));
    }

    return {
        minBet: ticketMpResponse.minBet,
        maxBet: ticketMpResponse.maxBet,
        balance: ticketMpResponse.balance ?? null,
        oddsInfo,
        errorCode: ticketMpResponse.errorCode,
        mpUid: ticketMpResponse.mpUid,
    };
}

function formatTicketOddsInfo(oddsInfo: TicketOddsInfo): ITicketOddsInfo {
    return {
        betCondition: oddsInfo.betCondition,
        liveAwayScore: oddsInfo.liveAwayScore,
        liveHomeScore: oddsInfo.liveHomeScore,
        oddsId: oddsInfo.oddsId,
        point: oddsInfo.point,
        price: oddsInfo.price,
        priceStyle: PriceStyle.fromPollux(oddsInfo.priceStyle),
        errorCode: oddsInfo.errorCode,
        uid: oddsInfo.uid,
    };
}

export function formatOrder(orderResponse: OrderResponse): IOrderResponse {
    return {
        isPlaceBetSuccess: orderResponse.isPlaceBetSuccess,
        transId: orderResponse.transId,
        placeOrderStatus: PlaceOrderStatus.fromPollux(orderResponse.placeOrderStatus),
        price: orderResponse.price,
        orderStatus: StatementDetailStatus.fromPollux(orderResponse.orderStatus),
        oddsId: orderResponse.oddsId,
        option: orderResponse.option,
        message: orderResponse.message ?? '',
        extraInfo: orderResponse.extraInfo,
    };
}

export function formatBetBuilderTicket(ticketResponse: BetBuilderTicketResponse): IBetBuilderTicketResponse {
    return {
        matchId: ticketResponse.matchId,
        markets: formatBetBuilderMarkets(ticketResponse.markets ?? []),
        minBet: ticketResponse.minBet ?? 0,
        maxBet: ticketResponse.maxBet ?? 0,
        price: formatPrice(ticketResponse.price),
        status: BetBuilderTicketStatus.fromPollux(ticketResponse.status),
        selectedSelections: ticketResponse.selectedMarketSelections?.map(formatSelectedSelection) ?? [],
        uid: ticketResponse.uid ?? '',
        balance: ticketResponse.balance ?? null,
    };
}

export function formatDynamicMarketTicket(ticketResponse: DynamicMarketTicketResponse): IDynamicMarketTicketResponse {
    return {
        balance: ticketResponse.balance ?? null,
        minBet: ticketResponse.minBet,
        maxBet: ticketResponse.maxBet,
        selection: {
            price: formatPrice(ticketResponse.selection?.price),
            betCondition: ticketResponse.selection?.betCondition ?? '',
            score: IScore.fromPollux(ticketResponse.selection?.score),
        },
        error: {
            code: OpenTicketStatus.fromPollux(ticketResponse.error.code),
            description: ticketResponse.error.description ?? '',
        },
        uid: ticketResponse.uid,
    };
}

export function formatBetBuilderOrder(order: BetBuilderOrderResponse): IBetBuilderOrderResponse {
    return {
        isPlaceBetSuccess: order.isPlaceBetSuccess,
        transId: order.transId,
        selectionIds: order.selectionIds ?? [],
        placeOrderStatus: PlaceOrderStatus.fromPollux(order.placeOrderStatus),
        orderStatus: StatementDetailStatus.fromPollux(order.orderStatus),
        price: formatPrice(order.price),
        isAccountClosed: order.isAccountClosed,
    };
}

export function formatDynamicMarketOrder(orderResponse: DynamicMarketOrderResponse): IDynamicMarketOrderResponse {
    return {
        isPlaceBetSuccess: orderResponse.isPlaceBetSuccess,
        transId: orderResponse.transId,
        placeOrderStatus: PlaceOrderStatus.fromPollux(orderResponse.placeOrderStatus),
        orderStatus: StatementDetailStatus.fromPollux(orderResponse.orderStatus),
        selectionId: orderResponse.selectionId,
        extraInfo: orderResponse.extraInfo,
    };
}

function formatBetBuilderMarkets(markets: BetBuilderMarketResponse[]): IBetBuilderBetSlipMarket[] {
    return markets.map<IBetBuilderBetSlipMarket>(market => ({
        matchMarketId: market.matchMarketId,
        selections: formatBetBuilderSelections(market.selections ?? []),
    }));
}

function formatBetBuilderSelections(markets: BetBuilderMarketSelectionResponse[]): IBetSlipSelection[] {
    return markets.map<IBetSlipSelection>(selection => ({
        id: selection.selectionId,
        status: BetBuilderSelectionStatus.fromPollux(selection.status),
        price: formatPrice(selection.price),
        isCompatible: selection.isCompatible,
    }));
}

function formatPrice(price: PriceResponse | undefined): IPrice | null {
    if (price) {
        return {
            value: price.value,
            priceStyle: PriceStyle.fromPollux(price.style),
        };
    }
    return null;
}

function formatSelectedSelection(selection: SelectedMarketSelectionResponse): IBetBuilderSelectedSelection {
    return {
        selectionId: selection.selectionId,
        status: BetBuilderSelectionStatus.fromPollux(selection.status),
        isRedundant: selection.isRedundant,
        liveScore: {
            home: selection.liveScore.home,
            away: selection.liveScore.away,
        },
    };
}
